import React, { useEffect, useState } from 'react';
import { Pressable, TouchableOpacity } from 'react-native';
import { AlucioChannel, DNABox, DNAButton, DNADivider, DNAText, Icon, Iffy } from '@alucio/lux-ui';
import { LuxSizeEnum } from '@alucio/lux-ui/src/typings';
import { CommonIcon } from 'src/components/DNA';
import InputComponent from 'src/components/Publishers/InputComponent';
import { styles, TextSearchPanelProps, useContentHookVariants } from '../TextSearchPanel';
import SearchResultsContent from '../SearchResultsContent';
import useFileTextSearch from 'src/hooks/useFileTextSearch/useFileTextSearch';
import useDebouncedState from 'src/hooks/useDebouncedState';
import { PresentationChannelMessage } from 'src/state/machines/presentation/playerWrapperTypes';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import type { BroadcastChannel } from 'broadcast-channel';

const INIT_SEARCH_VALUE = { initialValue: '' }

const DefaultVariant: React.FC<TextSearchPanelProps> = React.memo(({ onClose, contentHookVariant = 'default' }) => {
  const {
    setActiveSlideByPresentationPageNumber,
    activePresentation,
    contentPageData,
    meetingId,
  } = useContentHookVariants[contentHookVariant]()
  const {
    activeValue: searchText,
    setActiveValue: setSearchText,
    debouncedValue: debouncedSearchText,
    debouncingActive: searching,
  } = useDebouncedState(INIT_SEARCH_VALUE)

  const { matches, totalMatchCount } = useFileTextSearch(
    debouncedSearchText,
    contentPageData,
  )

  const [activeIndex, setActiveIndex] = useState<number>(0)

  const enableHighlightContentPlayerSearch = useFeatureFlags('BEAC_7756_highlight_content_player_search')
  const totalResults = matches.length

  useEffect(() => {
    if (totalResults && totalResults > 0) {
      const { presentationPageNumber } = matches[activeIndex]
      setActiveSlideByPresentationPageNumber(presentationPageNumber)
      const newSelectedPage = activePresentation?.presentable.presentablePages
        .find(p => p.presentationPageNumber === presentationPageNumber)

      newSelectedPage && analytics.track('DOCUMENT_SEARCH_SELECT', {
        action: 'SEARCH_SELECT',
        category: 'DOCUMENT',
        /** Verify with Eric if we also want to include presentation number & custom deck ID */
        pageNumber: newSelectedPage.page.number,
        searchTerm: searchText,
        documentId: newSelectedPage.documentVersionORM.model.documentId,
        documentVersionId: newSelectedPage.documentVersionORM.model.id,
      });
    }
  }, [activeIndex])

  const searchResultsVisible = searchText.length > 2

  useEffect(() => {
    if (!enableHighlightContentPlayerSearch)
      return

    const searchTerm = debouncedSearchText.length > 2 ? debouncedSearchText : '';
    // POC: reuse the existing presentation channel so the search panel can drive
    // visual highlights in whichever PDF/PPT iframe is currently active.
    const presentationChannel = AlucioChannel.get(
      AlucioChannel.commonChannels.PRESENTATION_CHANNEL,
    ) as BroadcastChannel<PresentationChannelMessage>

    const msg: PresentationChannelMessage = {
      type: 'SET_SEARCH_HIGHLIGHT',
      meetingId: meetingId || 'aaaaaa',
      searchTerm,
    }
    presentationChannel.postMessage(msg)

    return () => {
      presentationChannel.postMessage({
        ...msg,
        searchTerm: '',
      })
    }
  }, [debouncedSearchText, meetingId])

  const SearchResultsHeader = () => {
    if (!searchResultsVisible) return null
    return (
      <>
        <DNABox appearance="row" alignY="center" style={{ marginHorizontal: 16, marginBottom: 16 }}>
          <DNABox style={styles.menuButtonContainer}>
            <DNAButton
              appearance="ghost"
              status="tertiary"
              iconLeft="caret-up-fill"
              onPress={() => setActiveIndex((prev) => Math.max(0, prev - 1))}
              disabled={!matches.length || activeIndex < 0}
            />
            <DNADivider vertical />
            <DNAButton
              appearance="ghost"
              status="tertiary"
              iconLeft="caret-down-fill"
              onPress={() => matches && setActiveIndex((prev) => Math.min(matches.length - 1, prev + 1))}
              disabled={!matches.length}
            />
          </DNABox>
          {
            // [UI2] - Update brand when migrating current brand to primary
            searching ? <DNAText status="brand">Searching...</DNAText>
              : matches && totalMatchCount && totalResults
                ? <DNABox appearance="col">
                  <DNAText testID="cpm-search-results-count" bold>{totalMatchCount} matches</DNAText>
                  <DNAText style={styles.subtitle}>
                    Found on {totalResults} slides
                  </DNAText>
                </DNABox>
                : <DNAText status="danger">No results found</DNAText>
          }
        </DNABox>
        <DNADivider />
      </>

    )
  }

  return (
    <DNABox
      appearance="col"
      style={styles.mainContainer}
    >
      <DNABox appearance="row" style={{ marginLeft: -8 }} alignY="center">
        <InputComponent
          value={searchText}
          onChangeText={setSearchText}
          required={false}
          size={LuxSizeEnum.sm}
          autoFocus={true}
          placeHolder="Search"
          inputStyle={styles.searchBar}
          testID="my-content-panel-search-input"
          getLeftIconFunction={() => (
            <TouchableOpacity>
              <Icon style={styles.searchIcon} name="magnifying-glass" />
            </TouchableOpacity>
          )}
          getRightIconFunction={() => (
            <Iffy is={searchText}>
              <Pressable onPress={() => setSearchText('')}>
                <CommonIcon variant="close-circle" />
              </Pressable>
            </Iffy>
          )}
        />
        <DNAButton
          appearance="outline"
          status="tertiary"
          iconLeft="x"
          onPress={onClose}
          style={{ marginTop: 2, marginRight: 16 }}
        />
      </DNABox>

      {/* Search Results */}
      <SearchResultsHeader />
      {searchResultsVisible &&
        <SearchResultsContent
          matches={matches}
          searchQuery={debouncedSearchText}
          activeIndex={activeIndex}
          setActiveIndex={setActiveIndex}
          setActiveSlide={setActiveSlideByPresentationPageNumber}
        />}
    </DNABox>
  )
});

export default DefaultVariant;
